import { alpha, createTheme } from '@mui/material/styles';

export function createAppTheme(mode) {
  const isDark = mode === 'dark';
  const background = isDark ? '#060b14' : '#eef4ff';
  const paper = isDark ? '#0b111c' : '#ffffff';
  const elevated = isDark ? 'rgba(13, 20, 32, 0.9)' : 'rgba(255, 255, 255, 0.96)';
  const textPrimary = isDark ? '#f8fbff' : '#0b1220';
  const textSecondary = isDark ? '#93a6c3' : '#34445d';
  const border = isDark ? 'rgba(171, 194, 255, 0.14)' : 'rgba(46, 74, 122, 0.14)';
  const brand = isDark ? '#8cb6ff' : '#1f4fa3';
  const accent = isDark ? '#dfe9ff' : '#7ea9ff';
  const coral = isDark ? '#7da8ff' : '#204784';

  return createTheme({
    palette: {
      mode,
      primary: { main: brand },
      secondary: { main: accent },
      error: { main: coral },
      background: {
        default: background,
        paper,
      },
      text: {
        primary: textPrimary,
        secondary: textSecondary,
      },
      divider: border,
    },
    shape: {
      borderRadius: 22,
    },
    typography: {
      fontFamily: '"SF Pro Display", "SF Pro Text", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif',
      h2: { fontWeight: 700, letterSpacing: '-0.05em' },
      h3: { fontWeight: 700, letterSpacing: '-0.045em' },
      h4: { fontWeight: 700, letterSpacing: '-0.04em' },
      h5: { fontWeight: 700, letterSpacing: '-0.02em' },
      h6: { fontWeight: 700 },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: isDark
              ? 'radial-gradient(circle at top left, rgba(146, 187, 255, 0.16), transparent 28%), radial-gradient(circle at bottom right, rgba(146, 187, 255, 0.11), transparent 22%), linear-gradient(180deg, #050913, #090f18 44%, #060b14)'
              : 'radial-gradient(circle at top left, rgba(147, 188, 255, 0.22), transparent 24%), radial-gradient(circle at bottom right, rgba(112, 151, 217, 0.1), transparent 18%), linear-gradient(180deg, #f4f8ff, #edf3fd 52%, #f5f8fe)',
            color: textPrimary,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(paper, isDark ? 0.62 : 0.9),
            backdropFilter: 'blur(24px) saturate(160%)',
            color: textPrimary,
            boxShadow: 'none',
            borderBottom: `1px solid ${border}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: elevated,
            border: `1px solid ${border}`,
            boxShadow: isDark
              ? '0 18px 60px rgba(0, 0, 0, 0.34)'
              : '0 16px 40px rgba(66, 93, 138, 0.12)',
            backdropFilter: 'blur(22px) saturate(150%)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            paddingInline: 16,
          },
          contained: {
            background: isDark
              ? 'linear-gradient(180deg, #dfe8ff, #8bb5ff)'
              : 'linear-gradient(180deg, #18396e, #2758a4)',
            color: isDark ? '#07111f' : '#f8fbff',
            boxShadow: isDark
              ? '0 16px 36px rgba(114, 161, 255, 0.26)'
              : '0 14px 28px rgba(39, 88, 164, 0.22)',
          },
          outlined: {
            borderColor: border,
            backgroundColor: alpha('#ffffff', isDark ? 0.02 : 0.78),
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            backdropFilter: 'blur(18px)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: alpha(paper, 0.88),
            backdropFilter: 'blur(28px) saturate(150%)',
            borderRight: `1px solid ${border}`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 18,
              backgroundColor: alpha('#ffffff', isDark ? 0.04 : 0.92),
              backdropFilter: 'blur(18px)',
            },
          },
        },
      },
    },
  });
}
